import React, { createContext, useState, useContext, useEffect } from 'react';
import { omit } from 'lodash';

import { useAuth } from 'contexts/auth';
import { useUser } from 'contexts/user';
import { useWebsocket } from 'contexts/websocket';
import courseRequests from 'services/course';
import { TYPES } from 'utils/constants';
import { getUserPreferredFullName } from 'utils/helper';

const InstructorRoleContext = createContext();
const useInstructor = () => useContext(InstructorRoleContext);

const InstructorRoleProvider = (props) => {
    const { auth, currentUser } = useAuth();
    const { courseGroups, currentGroup, getCourseGroups, setCurrentGroup } = useUser();
    const { sendOn, taskWS } = useWebsocket();

    const [course, setCourse] = useState({});
    const [courseUsers, setCourseUsers] = useState([]);
    const [courseAccounts, setCourseAccounts] = useState([]);
    const [contributions, setContributions] = useState();
    const [distributions, setDistributions] = useState();
    const [suggestions, setSuggestions] = useState([]);
    const [courseChannels, setCourseChannels] = useState([]);
    const [taskTemplates, setTaskTemplates] = useState([]);
    const [milestones, setMilestones] = useState([]);
    const [courseLoading, setCourseLoading] = useState(true);

    const getCurrentCourse = async () => {
        if (!auth || !auth?.course_id) return;
        const currentCourse = await courseRequests.findCourse(auth.course_id);
        setCourse(currentCourse);
    };

    const getCourseAccounts = async () => {
        if (!auth || !auth?.course_id) {
            return [];
        }
        const currentCourseAccounts = await courseRequests.findCourseAccounts(auth.course_id);
        setCourseAccounts(currentCourseAccounts);
        return currentCourseAccounts;
    };

    const getCourseUsers = async () => {
        if (!auth || !auth?.course_id) return [];
        const currentCourseUsers = await courseRequests.findCourseUsers(auth.course_id);
        setCourseUsers(currentCourseUsers);
        return currentCourseUsers;
    };

    const getCourseSuggestions = async () => {
        if (!auth || !auth?.course_id) return setSuggestions([]);
        const currentCourseSuggestions = await courseRequests.listGroupStatuses(auth.course_id);
        setSuggestions(currentCourseSuggestions);
    };

    const getCourseChannels = async () => {
        if (!auth || !auth?.course_id) return setCourseChannels([]);
        const currentChannels = await courseRequests.listChannels(auth.course_id);
        setCourseChannels(currentChannels);
        return currentChannels;
    };

    const getTaskTemplates = async () => {
        if (!auth || !auth?.course_id) return setTaskTemplates([]);
        const currentCourseTaskTemplates = await courseRequests.listTaskTemplates(auth.course_id);
        setTaskTemplates(currentCourseTaskTemplates);
    };

    const getMilestones = async () => {
        if (!auth || !auth?.course_id) return setMilestones([]);
        const currentCourseMilestones = await courseRequests.listMilestones(auth.course_id);
        setMilestones(currentCourseMilestones);
    };

    const listContributions = async () => {
        if (!auth || !auth?.course_id) return;
        const contributionsReq = await courseRequests.findCourseContributions(auth.course_id);
        setContributions(contributionsReq);
        return contributionsReq;
    };

    const listDistributions = async () => {
        if (!auth || !auth?.course_id) return;
        const distributionsReq = await courseRequests.findCourseDistributions(auth.course_id);
        setDistributions(distributionsReq);
        return distributionsReq;
    };

    const getStatus = (group) => {
        const groupVariance = getPosition(group);
        if (groupVariance < -1) return 'offTrack';
        else if (groupVariance < 0) return 'caution';
        else return 'onTrack';
    };

    const getCourseGroupsById = () => {
        const groupsByIds = {};
        (courseGroups || []).forEach((item) => {
            groupsByIds[item.id] = item;
        });
        return groupsByIds;
    };

    const getUsersByAccountId = async () => {
        const accountData = (await getCourseAccountsByUserId()) || {};
        let userData = courseUsers;

        if (!userData) {
            userData = await getCourseUsers();
        }
        if (!accountData) return {};

        const result = {};
        userData.map((item) => {
            result[accountData[item.id]?.id] = {
                ...item,
                connections: accountData[item.id]?.connections,
                role: accountData[item.id]?.role,
            };
        });
        return result;
    };

    const getAllUsersByAccountId = async () => {
        let accounts = courseAccounts;
        let userData = courseUsers;
        if (!userData) userData = await getCourseUsers();

        return accounts.reduce((acc, account) => {
            return {
                ...acc,
                [account.id]: userData.find((user) => user.id === account.user_id),
            };
        }, {});
    };

    const getCourseAccountsByUserId = () => {
        const accountsByIds = {};
        let accounts = courseAccounts;
        (accounts || []).forEach((item) => {
            if (item.active) {
                accountsByIds[item.user_id] = item;
            }
        });

        return accountsByIds;
    };

    const getCourseAccountsById = () => {
        const accountsByIds = {};
        courseAccounts.map((item) => {
            accountsByIds[item.id] = item;
        });
        return accountsByIds;
    };

    const getCourseStudentsUsersAndAccounts = async () => {
        const accountData = await getCourseAccountsByUserId();
        let userData = courseUsers;

        if (!userData) {
            userData = await getCourseUsers();
        }
        const results = {};
        userData.forEach((item) => {
            if (accountData[item.id]?.role !== 'instructor') {
                const { id, active, ...rest } = accountData[item.id] || {};
                results[item.id] = { ...item, ...rest, active, owner_account_id: id };
            }
        });
        return results;
    };

    const getGroupUserAndAccounts = async (groupId) => {
        const accounts = getCourseAccountsByUserId();
        const localContributions = contributions ? contributions : await listContributions();
        const result = [];
        (courseUsers || []).forEach((item) => {
            const { id, group_id, role, ...rest } = accounts[item.id];
            if (groupId === group_id) {
                const individualContribution = localContributions[group_id]?.assigned[accounts[item.id]?.id] || {};
                result.push({
                    ...item,
                    owner_account_id: accounts[item.id].id,
                    group_id,
                    tasks: [
                        individualContribution?.low?.total || 0,
                        individualContribution?.medium?.total || 0,
                        individualContribution?.high?.total || 0,
                    ],
                    ...rest,
                });
            }
        });

        return result;
    };

    const getCourseInstructors = () => {
        const result = [];

        (courseUsers || []).forEach((user) => {
            const account = courseAccounts.find((account) => account.user_id === user.id && account.active);
            if (account?.role === 'instructor' || account?.role === 'ta') {
                result.push({
                    ...user,
                    owner_account_id: account.id,
                    ...omit(account, ['id']),
                });
            }
        });
        return result;
    };

    const getCurrentInstructorAccount = () => {};

    const getUserByAccountId = (accountId) => {
        const userId = courseAccounts.find(({ id }) => id === accountId)?.user_id;
        const userRole = courseAccounts.find(({ id }) => id === accountId)?.role;
        const userData = courseUsers.find(({ id }) => id === userId);
        if (userRole === 'instructor' || userRole === 'ta') {
            userData.role = userRole;
        }
        return userData;
    };

    const instructorsByAccountId = async () => {
        const getInstructors = await courseRequests.findCourseInstructors(auth.course_id);
        const result = {};
        getInstructors.map((item) => {
            result[item.owner_account_id] = item;
        });
        return result;
    };

    const getAssignableGroupMembers = (groupId) => {
        if (!groupId) return [];

        return courseAccounts
            .filter(({ group_id }) => group_id === groupId)
            .filter(({ active }) => active)
            .map((account) => {
                const member = courseUsers.find(({ id }) => id === account.user_id);

                return {
                    title: getUserPreferredFullName(member),
                    value: account.id,
                };
            });
    };

    const getGroupListWithAnalytics = async () => {
        let groups = courseGroups;
        if (!groups) groups = await getCourseGroups();
        return (groups || []).map((item) => {
            return {
                ...item,
                progress: getProgress(item.id),
                status: getStatus(item.id),
                position: getPosition(item.id),
                attention: [],
            };
        });
    };

    const getTotalPts = () => {
        if (courseGroups) {
            const totalPts = Object.entries(distributions || {}).reduce(
                (prev, [key, { unassigned = null, assigned = null }]) => {
                    const { completed = 0, total = 0 } = assigned?.points;
                    const progress = !total ? 0 : Math.ceil((completed * 100) / total);
                    return prev + progress;
                },
                0
            );
            return totalPts;
        }
    };

    const getAverage = () => {
        if (courseGroups) {
            let average = getTotalPts() / courseGroups.length;
            average = courseGroups && courseGroups.length > 0 ? average.toFixed(2) : 0;
            return average;
        }
    };

    const getDiffSum = () => {
        const average = getAverage();
        const diffSum = Object.entries(distributions || {}).reduce(
            (prev, [key, { unassigned = null, assigned = null }]) => {
                const { completed = 0, total = 0 } = assigned?.points;
                const progress = !total ? 0 : Math.ceil((completed * 100) / total);
                return prev + Math.pow(progress - average, 2);
            },
            0
        );
        return diffSum;
    };

    const getDeviation = () => {
        if (courseGroups) {
            const diffSum = getDiffSum();
            const sumAvg = courseGroups.length === 0 ? 0 : diffSum / courseGroups.length;
            const deviation = Math.sqrt(sumAvg);
            return deviation;
        }
        return null;
    };

    const getPosition = (groupId) => {
        const deviation = getDeviation();
        const average = getAverage();
        let distributionData = distributions;
        if (!distributionData) distributionData = listDistributions();
        if (distributionData) {
            var { points } = distributionData[groupId]?.assigned || {};
            var { completed = 0, total = 0 } = points || {};
            var progress = !total ? 0 : Math.ceil((completed / total) * 100);
            var position = deviation > 0 ? (progress - average) / deviation : 0;
            return position;
        }
        return 0;
    };

    const getProgress = (groupId) => {
        if (!groupId || !distributions || !distributions[groupId]) return 0;
        const completedPoints = distributions[groupId]?.assigned?.points?.completed;
        const totalPoints = distributions[groupId]?.assigned?.points?.total;
        var progress = !totalPoints ? 0 : ((completedPoints / totalPoints) * 100).toFixed();
        return progress;
    };

    const handleGroupClicked = (groupId) => {
        if (!groupId) {
            return setCurrentGroup(null);
        }
        const group = (courseGroups || []).find(({ id }) => id === groupId);
        setCurrentGroup(group);
    };

    const updateSelfUserInCourse = (selfUser) => {
        setCourseUsers((prev) => {
            return prev.map((user) => {
                return user.id === currentUser.id
                    ? {
                          ...user,
                          ...selfUser,
                      }
                    : user;
            });
        });
    };

    const createTaskTemplate = async (template) => {
        const { resources = [], ...rest } = template;
        await Promise.all(
            resources.map(async (resource) => {
                return resource.type === 'file'
                    ? await courseRequests.createCourseFiles(template.course_id, {
                          ...resource,
                          type: TYPES.TEMPLATE,
                          template_id: template.id,
                          public: false,
                      })
                    : await courseRequests.createCourseSharedUrls(template.course_id, {
                          ...resource,
                          type: TYPES.TEMPLATE,
                          template_id: template.id,
                          public: false,
                      });
            })
        );
        return await courseRequests.createCourseTaskTemplate(course.id, rest);
    };

    const updateTaskTemplate = (templateId, changes, shouldNotify = false) => {
        sendOn('default', {
            payload_type: 'update_template',
            payload: {
                course_id: course.id,
                template_id: templateId,
                changes,
                should_notify_email: shouldNotify,
            },
        });
    };

    const deleteTaskTemplate = (templateId) => {
        sendOn('default', {
            payload_type: 'delete_template',
            payload: {
                course_id: course.id,
                template_id: templateId,
            },
        });
    };

    const publishTaskTemplate = (templateId, changes) => {
        sendOn('default', {
            payload_type: 'publish_template',
            payload: {
                course_id: course.id,
                template_id: templateId,
                changes,
            },
        });
    };

    const unpublishTaskTemplate = (templateId) => {
        sendOn('default', {
            payload_type: 'unpublish_template',
            payload: {
                course_id: course.id,
                template_id: templateId,
            },
        });
    };

    const createMilestone = async (milestone) => {
        const { resources = [], ...rest } = milestone;
        await Promise.all(
            resources.map(async (resource) => {
                return resource.type === 'file'
                    ? await courseRequests.createCourseFiles(milestone.course_id, {
                          ...resource,
                          type: TYPES.MILESTONE,
                          milestone_id: milestone.id,
                          public: false,
                      })
                    : await courseRequests.createCourseSharedUrls(milestone.course_id, {
                          ...resource,
                          type: TYPES.MILESTONE,
                          milestone_id: milestone.id,
                          public: false,
                      });
            })
        );
        return await courseRequests.createCourseMilestone(course.id, rest);
    };

    const updateMilestone = (milestoneId, changes, shouldNotify = false) => {
        sendOn('default', {
            payload_type: 'update_milestone',
            payload: {
                course_id: course.id,
                milestone_id: milestoneId,
                changes,
                should_notify_email: shouldNotify,
            },
        });
    };

    const deleteMilestone = (milestoneId) => {
        sendOn('default', {
            payload_type: 'delete_milestone',
            payload: {
                course_id: course.id,
                milestone_id: milestoneId,
            },
        });
    };

    const publishMilestone = (milestoneId, changes) => {
        sendOn('default', {
            payload_type: 'publish_milestone',
            payload: {
                course_id: course.id,
                milestone_id: milestoneId,
                changes,
            },
        });
    };

    const unpublishMilestone = (milestoneId) => {
        sendOn('default', {
            payload_type: 'unpublish_milestone',
            payload: {
                course_id: course.id,
                milestone_id: milestoneId,
            },
        });
    };

    const initializeInstructorRole = async () => {
        await Promise.all([
            listContributions(),
            listDistributions(),
            getCurrentCourse(),
            getCourseGroups(),
            getCourseAccounts(),
            getCourseUsers(),
            getCourseSuggestions(),
            getTaskTemplates(),
            getMilestones(),
        ]);
        setCourseLoading(false);
    };

    useEffect(() => {
        if (auth) {
            initializeInstructorRole();
        }
    }, [auth, currentGroup?.id]);

    useEffect(() => {
        if (!taskWS) return;
        const { payload_type, payload } = taskWS;
        switch (payload_type) {
            case 'update_template':
                const { template: newTemplate } = payload;
                if (newTemplate) {
                    setTaskTemplates((prev) => {
                        return prev.map((template) => {
                            return template.id === newTemplate.id
                                ? { ...template, ...omit(newTemplate, ['published_on']) }
                                : template;
                        });
                    });
                }
                break;
            case 'update_milestone':
                const { milestone: newMilestone } = payload;
                if (newMilestone) {
                    setMilestones((current) => {
                        return current.map((milestone) => {
                            return milestone.id === newMilestone.id
                                ? { ...milestone, ...omit(newMilestone, ['published_on']) }
                                : milestone;
                        });
                    });
                }
                break;
            default:
                break;
        }
    }, [taskWS]);

    return (
        <InstructorRoleContext.Provider
            value={{
                course,
                courseUsers,
                courseAccounts,
                courseGroups,
                courseChannels,
                groupId: currentGroup?.id,
                contributions,
                distributions,
                suggestions,
                taskTemplates,
                milestones,
                getCurrentInstructorAccount,
                getCourseChannels,
                getCourseGroupsById,
                getUsersByAccountId,
                getAllUsersByAccountId,
                getCourseAccountsById,
                getCourseAccountsByUserId,
                getCourseStudentsUsersAndAccounts,
                getGroupUserAndAccounts,
                getUserByAccountId,
                getAssignableGroupMembers,
                getGroupListWithAnalytics,
                getTotalPts,
                getAverage,
                getDiffSum,
                getDeviation,
                getPosition,
                getStatus,
                getProgress,
                handleGroupClicked,
                initializeInstructorRole,
                setGroupId: handleGroupClicked,
                updateSelfUserInCourse,
                getCourseInstructors,
                instructorsByAccountId,
                courseLoading,
                listContributions,
                listDistributions,

                getTaskTemplates,
                getMilestones,
                setTaskTemplates,
                createTaskTemplate,
                updateTaskTemplate,
                deleteTaskTemplate,
                publishTaskTemplate,
                unpublishTaskTemplate,

                setMilestones,
                createMilestone,
                updateMilestone,
                deleteMilestone,
                publishMilestone,
                unpublishMilestone,
            }}
            {...props}
        />
    );
};

export { useInstructor };
export default InstructorRoleProvider;
